import axios from 'axios'
import { z } from 'zod'
import { getKycStatus } from '@api/api'
import { FlowCardLoading } from '@components/FlowCard'
import {
  KycError,
  KycTimeout,
  PostLoginExistingUser,
} from '@components/RouteComponents'
import {
  orgDetailsQueryOptions,
  orgSettingsQueryOptions,
} from '@src/queryOptions/org'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { rootSearchSchema } from '../-searchSchemas/rootSearchSchema'

const kycResultsSchema = rootSearchSchema.extend({
  inquiryId: z.preprocess(
    val => (val === undefined ? undefined : `${val}`),
    z.string().optional()
  ),
})

export const Route = createFileRoute('/_initialized/kycResults')({
  validateSearch: zodValidator(kycResultsSchema),
  loaderDeps: ({ search: { inquiryId } }) => ({ inquiryId }),
  loader: async ({
    deps: { inquiryId },
    context: { store, queryClient, fireMixpanelEvent },
    location,
  }) => {
    const kycRes = await getKycStatus(inquiryId)
    let status = kycRes.data.data?.status
    const {
      has_existing_account, //true if is linked to an account created by org
      under_age,
      message,
      passed,
      needs_review,
      attempted,
      address,
      allow_additional_documentation,
      additional_documentation_attempted,
      minor_accounts_allowed,
      paycard_account,
    } = kycRes.data.data
    const storeActions = store.getActions().onboarding
    const storeState = store.getState().onboarding
    storeActions.setEnteredAddress(address)
    storeActions.setIsMinorAccount(under_age)
    const search = location.search
    if ('inquiryId' in search) {
      //do not persist inquiryId in url
      delete search.inquiryId
    }
    const redirectProps = {
      search,
      replace: true,
      throw: true,
    }

    if (!!paycard_account) {
      !!has_existing_account && storeActions.setCanClaimWallet(true)
      storeActions.setEssentialFlow(true)
      redirect({
        to: '/passcode',
        ...redirectProps,
      })
    }

    while (status === 'completed') {
      //poll for new status https://github.com/branch-messenger/Branch-Web-Accounts/pull/133#discussion_r927139145
      await new Promise(resolve => setTimeout(resolve, 2500))
      const newResults = await getKycStatus(inquiryId)
      status = newResults.data.data.status
    }

    if (under_age && !minor_accounts_allowed) {
      redirect({
        to: '/kycMinor',
        ...redirectProps,
      })
    }

    if (passed) {
      fireMixpanelEvent('persona|kyc.passed')
      // if going through kyc, we can assume the user does not want an essential account
      storeActions.setEssentialFlow(false)
      if (has_existing_account) {
        //this will be true if the user already has an account created by the org, for example preKyc or limited account
        storeActions.setCanClaimWallet(true)
      }
      redirect({
        to: '/passcode',
        ...redirectProps,
      })
    }

    if (needs_review) {
      storeActions.setAccountNeedsReview(true)
      redirect({
        to: '/kycPending',
        ...redirectProps,
      })
    }

    if (!attempted) {
      redirect({ to: '/persona', ...redirectProps })
    }

    // edge case where preKyc account fails kyc, enter essential flow https://branchmessenger.atlassian.net/browse/PAYENT-18483
    if (attempted && !allow_additional_documentation && has_existing_account) {
      storeActions.setCanClaimWallet(true)
      storeActions.setEssentialFlow(true)
      redirect({
        to: '/passcode',
        ...redirectProps,
      })
    }

    if (
      attempted &&
      !additional_documentation_attempted &&
      status !== 'declined'
    ) {
      storeActions.setAccountNeedsAdditionalDocumentation(true)
      redirect({ to: '/persona', ...redirectProps })
    }

    const orgDetails = await queryClient.ensureQueryData(
      orgDetailsQueryOptions({ organizationId: storeState.orgId })
    )
    const location_id = orgDetails?.data.default_location_id
    const orgSettings = await queryClient.ensureQueryData(
      orgSettingsQueryOptions(location_id)
    )
    const canRouteToPayCard = orgSettings?.data?.enable_paycard

    if (attempted && canRouteToPayCard) {
      !!has_existing_account && storeActions.setCanClaimWallet(true)
      redirect({
        to: '/kycFailedEssentialAvailable',
        ...redirectProps,
      })
    }

    if (attempted && !!message) {
      redirect({
        to: '/kycFailed',
        ...redirectProps,
      })
    }
  },
  errorComponent: ({ error }: { error: any }) => {
    if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
      return <KycTimeout />
    }
    return <KycError />
  },
  pendingMs: 0,
  pendingComponent: FlowCardLoading,
  component: PostLoginExistingUser,
})
